import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  InputAdornment,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  TextField,
  Typography,
} from "@mui/material";
import { Clear, ExpandLess, ExpandMore, Search } from "@mui/icons-material";
import MDPreview from "@uiw/react-markdown-preview/nohighlight";
import rehypeSanitize from "rehype-sanitize";
import { useTranslation } from "react-i18next";

import { DrawerHeader } from "../Common/styles/drawer";
import { normalizeUiLang } from "../../utils/studyName";
import { useAppDispatch, useAppSelector } from "../../Redux/hooks";
import { closeHelp } from "../../Redux/reducers/helpSlice";
import {
  getSectionContent,
  getSectionTitle,
  HELP_MANUAL_CATEGORIES,
  HELP_MANUAL_SECTIONS,
} from "./manualContent";
import { STUDY_NAV_GROUPS } from "./studyNavGroups";
import type { HelpManualCategory } from "./manualContent";

type Props = {
  /** When true, show only Platform Administration topics; otherwise only Study Management. */
  isPlatform?: boolean;
};

const HelpManualDrawer = ({ isPlatform = false }: Props) => {
  const { t, i18n } = useTranslation("sidebar");
  const locale = normalizeUiLang(i18n.language);
  const dispatch = useAppDispatch();
  const open = useAppSelector((state) => state.help.isOpen);
  const selectedSectionId = useAppSelector(
    (state) => state.help.selectedSectionId,
  );
  const studyFeatures = useAppSelector((state) => state.study.features);
  const studyPermissions = useAppSelector(
    (state) => state.study.studyPermissions,
  );
  const platformPermissions = useAppSelector(
    (state) => state.user.platformPermissions,
  );
  const manualCategory: HelpManualCategory = isPlatform ? "platform" : "study";
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | undefined>(
    selectedSectionId,
  );
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const itemRefs = useRef<Record<string, HTMLElement | null>>({});
  const wasOpenRef = useRef(false);

  const scrollToId = (id?: string) => {
    if (!id) return;
    itemRefs.current[id]?.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const toggleGroup = (groupId: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  };

  const localizedSections = useMemo(
    () =>
      HELP_MANUAL_SECTIONS.map((section) => ({
        id: section.id,
        category: section.category,
        featureKey: section.featureKey,
        permissionKey: section.permissionKey,
        platformPermissionKey: section.platformPermissionKey,
        title: getSectionTitle(section, locale),
        content: getSectionContent(section, locale),
      })),
    [locale],
  );

  // Context (platform vs study) + same feature/RBAC gates as the matching sidebar.
  const accessibleSections = useMemo(
    () =>
      localizedSections.filter((section) => {
        if (section.category !== manualCategory) return false;
        if (section.featureKey && !studyFeatures?.[section.featureKey]) {
          return false;
        }
        if (
          section.permissionKey &&
          !studyPermissions.includes(section.permissionKey)
        ) {
          return false;
        }
        if (
          section.platformPermissionKey &&
          !platformPermissions.includes(section.platformPermissionKey)
        ) {
          return false;
        }
        return true;
      }),
    [
      localizedSections,
      manualCategory,
      studyFeatures,
      studyPermissions,
      platformPermissions,
    ],
  );

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }
    const fallbackId = accessibleSections[0]?.id;
    const nextSelectedId =
      (selectedSectionId &&
        accessibleSections.some((s) => s.id === selectedSectionId) &&
        selectedSectionId) ||
      fallbackId;
    setSelectedId(nextSelectedId);

    const owningGroup = STUDY_NAV_GROUPS.find((group) =>
      group.topicIds.includes(nextSelectedId ?? ""),
    );
    if (owningGroup) {
      setExpandedGroups((prev) =>
        prev.has(owningGroup.id) ? prev : new Set(prev).add(owningGroup.id),
      );
    }

    // The drawer's slide-in (and, if the group wasn't already expanded, its own
    // Collapse animation) are still running right after `open` flips true — calling
    // scrollIntoView mid-transition miscalculates position in some browsers. When
    // those transitions are the only thing happening, their onEntered handlers below
    // do the scrolling instead. But if the drawer was already open (no transition
    // fires), do it here.
    if (wasOpenRef.current) {
      const raf = requestAnimationFrame(() => scrollToId(nextSelectedId));
      wasOpenRef.current = true;
      return () => cancelAnimationFrame(raf);
    }
    wasOpenRef.current = true;
  }, [open, selectedSectionId, accessibleSections]);

  const filteredSections = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return accessibleSections;
    }
    return accessibleSections.filter(
      (section) =>
        section.title.toLowerCase().includes(query) ||
        section.content.toLowerCase().includes(query),
    );
  }, [accessibleSections, search]);

  const sectionsById = useMemo(() => {
    const map: Record<string, (typeof filteredSections)[number]> = {};
    filteredSections.forEach((section) => {
      map[section.id] = section;
    });
    return map;
  }, [filteredSections]);

  const platformSections = useMemo(
    () => filteredSections.filter((section) => section.category === "platform"),
    [filteredSections],
  );

  // Groups whose members all got filtered out (by search, feature flags, or RBAC) drop out entirely.
  const studyGroups = useMemo(
    () =>
      STUDY_NAV_GROUPS.map((group) => ({
        ...group,
        sections: group.topicIds
          .map((id) => sectionsById[id])
          .filter((section): section is (typeof filteredSections)[number] => !!section),
      })).filter((group) => group.sections.length > 0),
    [sectionsById],
  );

  const selectedSection =
    filteredSections.find((section) => section.id === selectedId) ||
    filteredSections[0];

  const handleClose = () => {
    setSearch("");
    dispatch(closeHelp());
  };

  const renderTopicItem = (section: (typeof filteredSections)[number]) => (
    <ListItemButton
      key={section.id}
      ref={(el) => {
        itemRefs.current[section.id] = el;
      }}
      selected={selectedSection?.id === section.id}
      onClick={() => setSelectedId(section.id)}
    >
      <ListItemText primary={section.title} />
    </ListItemButton>
  );

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      SlideProps={{ onEntered: () => scrollToId(selectedId) }}
    >
      <Box
        sx={{
          width: { xs: "100vw", md: "85vw" },
          height: "100%",
          p: "16px 24px 0 24px",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <DrawerHeader closeDrawer={handleClose} title={t("USER_MANUAL_TITLE")} />
        <Box sx={{ display: "flex", flex: 1, minHeight: 0, gap: 2, pb: 2 }}>
          <Box
            sx={{
              width: 300,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              borderRight: "1px solid",
              borderColor: "divider",
              pr: 1,
            }}
          >
            <TextField
              size="small"
              placeholder={t("USER_MANUAL_SEARCH_PLACEHOLDER")}
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <Clear
                      fontSize="small"
                      sx={{ cursor: "pointer" }}
                      onClick={() => setSearch("")}
                    />
                  </InputAdornment>
                ) : undefined,
              }}
            />
            <Box sx={{ overflow: "auto", flex: 1 }}>
              {filteredSections.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  {t("USER_MANUAL_NO_RESULTS")}
                </Typography>
              ) : (
                <List dense disablePadding>
                  {platformSections.length > 0 && (
                    <li key="platform">
                      <ul style={{ padding: 0 }}>
                        <ListSubheader disableSticky>
                          {t(HELP_MANUAL_CATEGORIES.platform)}
                        </ListSubheader>
                        {platformSections.map(renderTopicItem)}
                      </ul>
                    </li>
                  )}
                  {studyGroups.length > 0 && (
                    <li key="study">
                      <ul style={{ padding: 0 }}>
                        <ListSubheader disableSticky>
                          {t(HELP_MANUAL_CATEGORIES.study)}
                        </ListSubheader>
                        {studyGroups.map((group) => {
                          if (group.sections.length === 1) {
                            return renderTopicItem(group.sections[0]);
                          }
                          const isGroupOpen =
                            !!search.trim() || expandedGroups.has(group.id);
                          return (
                            <Box key={group.id}>
                              <ListItemButton onClick={() => toggleGroup(group.id)}>
                                <ListItemText primary={group.label ?? group.id} />
                                {isGroupOpen ? (
                                  <ExpandLess fontSize="small" />
                                ) : (
                                  <ExpandMore fontSize="small" />
                                )}
                              </ListItemButton>
                              <Collapse
                                in={isGroupOpen}
                                timeout="auto"
                                onEntered={() => {
                                  if (
                                    selectedId &&
                                    group.topicIds.includes(selectedId)
                                  ) {
                                    scrollToId(selectedId);
                                  }
                                }}
                              >
                                <Box sx={{ pl: 2 }}>
                                  {group.sections.map(renderTopicItem)}
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </ul>
                    </li>
                  )}
                </List>
              )}
            </Box>
          </Box>
          <Box
            data-color-mode="light"
            sx={{
              flex: 1,
              overflow: "auto",
              pl: 2,
              "& .wmde-markdown": {
                bgcolor: "transparent",
                color: "inherit",
              },
              "& img": {
                maxWidth: "100%",
                borderRadius: 1,
                border: "1px solid",
                borderColor: "divider",
                my: 1,
              },
            }}
          >
            {selectedSection ? (
              <MDPreview
                source={selectedSection.content}
                wrapperElement={{ "data-color-mode": "light" }}
                rehypePlugins={[[rehypeSanitize]]}
                style={{ background: "transparent", color: "inherit" }}
              />
            ) : null}
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default HelpManualDrawer;
